import type { ReactNode } from 'react';
import { cx } from '../cx';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { CHART_MINIMUM, TICK_TEXT } from './scale';

const DOT_MAX: Record<ChartLayout, string> = { auto: 'max-w-4 @md:max-w-6', narrow: 'max-w-4', wide: 'max-w-6' };

/**
 * Twenty values that each stand for one in twenty ads: the 2.5th, 7.5th, ... 97.5th percentiles.
 * Counting dots is how people read this chart, so the count of dots below a value is the headline.
 */
export function quantileDots(values: number[], count = 20): number[] {
  const s = [...values].sort((a, b) => a - b);
  if (!s.length) return [];
  return Array.from({ length: count }, (_, i) => {
    const q = (i + 0.5) / count;
    const at = q * (s.length - 1);
    const lo = Math.floor(at);
    const hi = Math.min(s.length - 1, lo + 1);
    return s[lo] + (s[hi] - s[lo]) * (at - lo);
  });
}

export type QuantileDotsProps = {
  /** One value per ad; needs at least 20. */
  values: number[];
  /** The width of a bin, a round number (€300). */
  binWidth: number;
  /** Dots below this value are the finding: sun with an ink ring. Count them with `quantileDots`. */
  below?: number;
  formatTick: (value: number) => string;
  /** Said instead of the chart when there are fewer than 20 values. */
  tooFew: ReactNode;
  /** `auto` (default) switches to the narrow form in a narrow container: smaller dots, fewer bin labels. */
  layout?: ChartLayout;
  /** One sentence with the count ("6 of 20 dots are under €3,600"), for screen readers. */
  description: string;
  className?: string;
};

/**
 * Twenty dots, each one in twenty ads, stacked into bins: a distribution people read by counting
 * ("about 4 in 20 pay under €3,600"). Quantile dotplots beat density plots for decisions. Static on
 * purpose: no animation.
 */
export function QuantileDots({
  values,
  binWidth,
  below,
  formatTick,
  tooFew,
  layout = 'auto',
  description,
  className,
}: QuantileDotsProps) {
  if (values.length < CHART_MINIMUM.spread) return <p className="type-meta text-jc-ink-secondary">{tooFew}</p>;
  const dots = quantileDots(values);
  const first = Math.floor(dots[0] / binWidth);
  const last = Math.floor(dots[dots.length - 1] / binWidth);
  const bins = Array.from({ length: last - first + 1 }, (_, i) => ({
    start: (first + i) * binWidth,
    dots: dots.filter((d) => Math.floor(d / binWidth) === first + i),
  }));
  const tall = Math.max(...bins.map((b) => b.dots.length));
  // Every bin is labelled with its middle; on wide runs only every second or third, evenly.
  const every = Math.max(1, Math.ceil(bins.length / 6));
  const everyNarrow = Math.max(1, Math.ceil(bins.length / 3));
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex items-end gap-0.5" aria-hidden="true">
        {bins.map((b) => (
          <div key={b.start} className="flex flex-1 flex-col-reverse items-center gap-0.5">
            {b.dots.map((d, k) => (
              <span
                key={k}
                className={cx(
                  'block aspect-square w-full rounded-full',
                  DOT_MAX[layout],
                  below != null && d < below ? 'bg-jc-chart-2 ring-2 ring-jc-ink ring-inset' : 'bg-jc-chart-context',
                )}
              />
            ))}
            {/* Keeps every bin the same height so the baseline lines up. */}
            {Array.from({ length: tall - b.dots.length }, (_, k) => (
              <span key={`e${k}`} className={cx('block aspect-square w-full', DOT_MAX[layout])} />
            ))}
          </div>
        ))}
      </div>
      <div className="flex gap-0.5 border-t border-jc-chart-grid pt-1" aria-hidden="true">
        {bins.map((b, i) => (
          <span key={b.start} className={cx('flex-1 text-center whitespace-nowrap', TICK_TEXT)}>
            {i % every === 0 || i % everyNarrow === 0 ? (
              <span
                className={
                  i % every !== 0 ? NARROW_ONLY[layout] : i % everyNarrow !== 0 ? WIDE_ONLY[layout] : undefined
                }
              >
                {formatTick(b.start + binWidth / 2)}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
}
